import { useMemo } from 'react'
import { UNMATCHED_DOORS_COLUMNS } from './UnmatchedDoorsColumnsInfo'
import { createTableColumnHelper } from '../Common/commonColumnHelpers'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { ContactCompanyType, IDoorProxy } from 'models/contact_companies'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { Link } from 'react-router-dom'
import { DataTableColumnSort } from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { PhoneCell } from 'components/Tables/CommonTableCells/PhoneCell'
import { getCommonDoorProxyColumns } from '../Common/CommonTable/CommonColumns'

export const useUnmatchedDoorsTableColumns = ({
  sorting,
  inCampaigns,
}: {
  sorting?: DataTableColumnSort[]
  inCampaigns?: boolean
} = {}) => {
  const { preferences } = usePreferences()
  const featureFlag = useFeatureFlagService()
  const tooltips: Record<string, unknown> = preferences?.tooltips || {}
  const columnHelper = createColumnHelper<IDoorProxy>()
  const unmatchedDoorsColumnHelper = createTableColumnHelper({
    columns: UNMATCHED_DOORS_COLUMNS,
    tooltips,
  })

  const columns = useMemo(() => {
    // Basic door information columns
    const baseColumns = [
      columnHelper.accessor('name', {
        ...unmatchedDoorsColumnHelper.retrieveColumn('name', {
          meta: {
            isEditable: false,
            isFirstColumn: true,
          },
        }),
        header: () => null,
        size: 250,
        cell: (info) => {
          const door = info.row.original
          const prefix =
            preferences?.company_type_prefix_map[
              ContactCompanyType.UserGenerated
            ]
          return door.id ? (
            <Link
              to={`/door/${prefix}${door.id}`}
              className="text-blue-600 hover:underline"
            >
              {info.getValue() || '-'}
            </Link>
          ) : (
            info.getValue() || '-'
          )
        },
      }),
      columnHelper.accessor('domain', {
        ...unmatchedDoorsColumnHelper.retrieveColumn('domain'),
      }),
      columnHelper.accessor('address', {
        ...unmatchedDoorsColumnHelper.retrieveColumn('address'),
      }),
      columnHelper.accessor('city', {
        ...unmatchedDoorsColumnHelper.retrieveColumn('city'),
      }),
      columnHelper.accessor('state', {
        ...unmatchedDoorsColumnHelper.retrieveColumn('state'),
      }),
      columnHelper.accessor('zipcode', {
        ...unmatchedDoorsColumnHelper.retrieveColumn('zipcode'),
      }),
      columnHelper.accessor('country', {
        ...unmatchedDoorsColumnHelper.retrieveColumn('country'),
      }),
      columnHelper.accessor('phone_number', {
        ...unmatchedDoorsColumnHelper.retrieveColumn('phone_number'),
        cell: (info) => {
          const phone = info.getValue()
          const extension = info.row.original.extension
          return <PhoneCell phoneNumber={phone} extension={extension} />
        },
      }),
      columnHelper.accessor('chain_size', {
        ...unmatchedDoorsColumnHelper.retrieveColumn('chain_size', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 110,
        cell: (info) => info.getValue() || '-',
        enableSorting: true,
      }),
    ]

    // Get common door proxy columns with type cast to handle TypeScript errors
    const commonColumns = getCommonDoorProxyColumns(
      columnHelper as any,
      sorting,
      preferences,
      inCampaigns
    ) as ColumnDef<IDoorProxy>[]

    // Filter out campaign columns if feature flag is disabled
    const filteredCommonColumns = !featureFlag.enableCampaigns
      ? commonColumns.filter((col) => col.id !== 'chain_proxy.campaigns')
      : commonColumns

    // Combine the base columns and common columns
    return [...baseColumns, ...filteredCommonColumns]
  }, [
    sorting,
    preferences,
    inCampaigns,
    featureFlag.enableCampaigns,
    columnHelper,
    unmatchedDoorsColumnHelper,
  ])

  return columns as ColumnDef<IDoorProxy>[]
}
